import { filter } from 'lodash-es'
import { browserName, browserVersion } from '@/helpers/device'
import { ENV, ENV_DEV_OR_STAGING, VUE_APP_VERSION } from '@/helpers/environment'
import { useMainStore } from '@/store/main'
import { useOnetrustStore } from '@/store/onetrust'
import {
	init,
	captureException,
	captureMessage,
	setTag,
	setExtra,
	setUser,
	withScope,
	captureConsoleIntegration,
	extraErrorDataIntegration,
} from '@sentry/browser'

/**
 * Filter console warnings except console.warn on /survey/plan_ready
 */
function shouldFilterConsoleWarn(event) {
	// vue-i18n v9 deprecated getter warnings triggered by Sentry's own _dropUndefinedKeys
	if (event.message?.includes('[intlify]')) {
		return true
	}

	if (event.level === 'warning' && event.logger === 'console') {
		if (event.request?.url.includes('/plan')) {
			return (
				event.message?.includes('Pixel') ||
				event.message?.includes('This device does not support Apple Pay') ||
				event.message?.includes('This device is not capable of making Apple Pay payments') ||
				event.message?.includes('Duplicate named routes definition') ||
				event.message?.includes('Meta pixel')
			)
		}

		return true
	}

	return false
}

/**
 * Filter console errors
 */
function shouldFilterConsoleError(event) {
	return [
		event.level === 'error',
		event.logger === 'console',
		/^Base\..*:\s+/.test(event.message), // Filter Base.*:* errors
	].every(Boolean)
}

const BREADCRUMBS_URLS_BLACKLIST = [
	'https://ct.pinterest.com',
	'https://cdn-ukwest.onetrust.com',
	'https://analytics.google.com',
	'https://tr.snapchat.com',
	'//tr.outbrain.com',
]

function shouldFilterNetworkErrors(event, error) {
	const errorName = error?.originalException?.name
	const errorMessage = error?.originalException?.message

	if (
		[
			errorName === 'TypeError' && ['Failed to fetch', 'Load failed'].includes(errorMessage),
			errorName === 'AxiosError' && ['Network Error'].includes(errorMessage),
		].some(Boolean)
	) {
		const errorBreadcrumbs = filter(event.breadcrumbs, { category: 'fetch', level: 'error' })

		return errorBreadcrumbs.every((breadcrumb) =>
			BREADCRUMBS_URLS_BLACKLIST.some((url) => breadcrumb.data?.url?.startsWith(url)),
		)
	}

	// FIXME temporary mute /user/token network errors
	if (event?.tags.networkErrorStatus === 0 && event?.tags.networkErrorUrl?.endsWith('/user/token')) {
		return true
	}

	return false
}

function shouldFilterByBrowserVersion() {
	const userAgent = window.navigator.userAgent

	// Filter Google Read-Aloud bot
	if (userAgent.includes('Google-Read-Aloud')) {
		return true
	}

	const browsersBlacklist = {
		'Chrome Mobile': 59,
	}

	if (browserName in browsersBlacklist) {
		return browserVersion <= browsersBlacklist[browserName]
	}

	return false
}

class Sentry {
	constructor() {
		this._instance = null

		this._isInited = new Promise((resolve) => {
			this._isInitedResolve = resolve
		})

		this.#init()
	}

	async #init() {
		this._instance = {
			captureException,
			captureMessage,
			setTag,
			setExtra,
			setUser,
			withScope,
		}

		// mainStore may not be available yet (Pinia not installed during module load).
		// Safely attempt to read initial values; fall back to defaults.
		let countryCode = ''
		let currencyCode = ''
		let userId = ''
		try {
			const mainStore = useMainStore()
			countryCode = mainStore.getCountryCodeUppercase
			currencyCode = mainStore.getCurrencyUppercase
			userId = mainStore.getUserId
		} catch {
			// Pinia not yet installed — will be set later via setTag/setUser
		}

		init({
			dsn: 'https://eaf1a8decc454276894d6dd63066bb96@o1383508.ingest.sentry.io/4505087656919044',
			autoSessionTracking: false,
			environment: ENV,
			release: VUE_APP_VERSION,
			integrations: [
				extraErrorDataIntegration({}),
				captureConsoleIntegration({
					levels: ['error', 'warn'],
				}),
			],
			initialScope: {
				tags: {
					countryCode,
					currencyCode,
					language: window.language,
				},
				user: {
					id: userId,
				},
			},
			ignoreErrors: [
				'TypeError: cancelled',
				'AxiosError: Request aborted',
				'getBattery',
				'Object Not Found Matching Id',
				'AbortError',
				"ReferenceError: Can't find variable: OffscreenCanvas", // GTM axon
				"TypeError: null is not an object (evaluating 'context.getExtension')", // GTM axon
				/^undefined is not an object \(evaluating '.\.(bannerGroup|Domain|DomainData).*$/, // OneTrust errors
				"Cannot read properties of undefined (reading 'domInteractive')", // SnapChat GTM error
				/^Attempted to mount element inside container .* but it could not be found on the page/, // Palta payments SDK
				/^Attempted to mount checkout UI at .* but the element could not be found/, // Palta payments SDK
				"TypeError: Cannot read properties of null (reading 'style')", // @palta-brain/web-sdk/dist/cjs/src/BrainTreeClient FIXME: remove after high season
				'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection', // GTM error
				'Error: Error initializing Braintree',
				'global_session_not_found',
				// Vue 3 + vue-i18n v9 legacy mode: mixin sets this.$n but Vue 3 proxy
				// blocks $-prefixed set when instance.n exists (from $nextTick cache).
				// Harmless — $n still works via globalProperties.
				/trap returned falsish for property '\$n'/,
				// 'h is not a function' — only muted locally (see beforeSend)
			],
			beforeSend: (event, error) => {
				// Transient Vite HMR render error — mute only on localhost
				if (VUE_APP_VERSION === 'local' && error?.originalException?.message?.includes('h is not a function')) {
					return null
				}

				if (shouldFilterConsoleWarn(event)) {
					return null
				}

				if (shouldFilterConsoleError(event)) {
					return null
				}

				if (shouldFilterNetworkErrors(event, error)) {
					return null
				}

				if (shouldFilterByBrowserVersion(event, error)) {
					return null
				}

				if (ENV_DEV_OR_STAGING) {
					let message = event.transaction || event.message || error?.originalException?.message

					if (message && message.length > 60) {
						message = `${message.substr(0, 60)}…`
					}

					/* eslint-disable no-console */
					console.groupCollapsed(`%cSentry Logger [${event.level}]: ${message}`, 'color: #f00')
					console.log(event)
					console.log(error?.originalException)
					console.groupEnd()
					/* eslint-enable no-console */
				}

				return event
			},
		})

		this._isInitedResolve()
	}

	/**
	 * @param {*} exception
	 * @returns {Promise<void>}
	 */
	async captureException(exception) {
		await this._isInited

		this._instance.captureException(exception)
	}

	/**
	 * @param {String} message
	 * @returns {Promise<void>}
	 */
	async captureMessage(message) {
		await this._isInited

		this._instance.captureMessage(message)
	}

	/**
	 * @param {Function} callback
	 * @returns {Promise<void>}
	 */
	async withScope(callback) {
		await this._isInited

		this._instance.withScope(callback)
	}

	/**
	 * @param {String} tagName
	 * @param {any} value
	 * @returns {Promise<void>}
	 */
	async setTag(tagName, value) {
		await this._isInited

		this._instance.setTag(tagName, value)
	}

	/**
	 * @returns {Promise<void>}
	 */
	async setUser() {
		await this._isInited

		const mainStore = useMainStore()
		const onetrustStore = useOnetrustStore()

		if (!mainStore.getUserId || !onetrustStore.isActiveConsentByName('functional_cookies')) {
			return
		}

		this._instance.setUser({ id: mainStore.getUserId, device_id: window.deviceId })
	}

	/**
	 * @param {String} key
	 * @param {any} value
	 * @returns {Promise<void>}
	 */
	async setExtra(key, value) {
		await this._isInited

		this._instance.setExtra(key, value)
	}
}

/** @type Sentry */
const sentry = new Sentry()

export default sentry
